// QuickBooks Online account types, their classification and the detail types (AccountSubType) each allows.
// Values are what QuickBooks stores and returns: the type as text ("Accounts Payable"), the detail type as a code ("AccountsPayable").
export const QBO_ACCOUNT_TYPES = [
  { type: 'Bank', classification: 'Asset', subTypes: ['CashOnHand', 'Checking', 'MoneyMarket', 'RentsHeldInTrust', 'Savings', 'TrustAccounts'] },
  { type: 'Accounts Receivable', classification: 'Asset', subTypes: ['AccountsReceivable'] },
  {
    type: 'Other Current Asset',
    classification: 'Asset',
    subTypes: [
      'AllowanceForBadDebts', 'DevelopmentCosts', 'EmployeeCashAdvances', 'Inventory', 'Investment_MortgageRealEstateLoans',
      'Investment_Other', 'Investment_TaxExemptSecurities', 'Investment_USGovernmentObligations', 'LoansToOfficers', 'LoansToOthers',
      'LoansToStockholders', 'OtherCurrentAssets', 'PrepaidExpenses', 'Retainage', 'UndepositedFunds',
    ],
  },
  {
    type: 'Fixed Asset',
    classification: 'Asset',
    subTypes: [
      'AccumulatedAmortization', 'AccumulatedDepletion', 'AccumulatedDepreciation', 'Buildings', 'DepletableAssets', 'FixedAssetComputers',
      'FixedAssetCopiers', 'FixedAssetFurniture', 'FixedAssetOtherToolsEquipment', 'FixedAssetPhone', 'FixedAssetPhotoVideo', 'FixedAssetSoftware',
      'FurnitureAndFixtures', 'IntangibleAssets', 'Land', 'LeaseholdImprovements', 'MachineryAndEquipment', 'OtherFixedAssets', 'Vehicles',
    ],
  },
  {
    type: 'Other Asset',
    classification: 'Asset',
    subTypes: ['AccumulatedAmortizationOfOtherAssets', 'Goodwill', 'LeaseBuyout', 'Licenses', 'OrganizationalCosts', 'OtherLongTermAssets', 'SecurityDeposits'],
  },
  { type: 'Accounts Payable', classification: 'Liability', subTypes: ['AccountsPayable'] },
  { type: 'Credit Card', classification: 'Liability', subTypes: ['CreditCard'] },
  {
    type: 'Other Current Liability',
    classification: 'Liability',
    subTypes: [
      'DirectDepositPayable', 'FederalIncomeTaxPayable', 'GlobalTaxPayable', 'GlobalTaxSuspense', 'InsurancePayable', 'LineOfCredit', 'LoanPayable',
      'OtherCurrentLiabilities', 'PayrollClearing', 'PayrollTaxPayable', 'PrepaidExpensesPayable', 'RentsInTrustLiability', 'SalesTaxPayable',
      'StateLocalIncomeTaxPayable', 'TrustAccountsLiabilities',
    ],
  },
  { type: 'Long Term Liability', classification: 'Liability', subTypes: ['NotesPayable', 'OtherLongTermLiabilities', 'ShareholderNotesPayable'] },
  {
    type: 'Equity',
    classification: 'Equity',
    subTypes: [
      'AccumulatedAdjustment', 'CommonStock', 'EstimatedTaxes', 'Healthcare', 'OpeningBalanceEquity', 'OwnersEquity', 'PaidInCapitalOrSurplus',
      'PartnerContributions', 'PartnerDistributions', 'PartnersEquity', 'PersonalExpense', 'PersonalIncome', 'PreferredStock', 'RetainedEarnings',
      'TreasuryStock',
    ],
  },
  {
    type: 'Income',
    classification: 'Revenue',
    subTypes: ['DiscountsRefundsGiven', 'NonProfitIncome', 'OtherPrimaryIncome', 'SalesOfProductIncome', 'ServiceFeeIncome', 'UnappliedCashPaymentIncome'],
  },
  {
    type: 'Other Income',
    classification: 'Revenue',
    subTypes: ['DividendIncome', 'InterestEarned', 'OtherInvestmentIncome', 'OtherMiscellaneousIncome', 'TaxExemptInterest'],
  },
  {
    type: 'Cost of Goods Sold',
    classification: 'Expense',
    subTypes: ['CostOfLaborCos', 'EquipmentRentalCos', 'OtherCostsOfServiceCos', 'ShippingFreightDeliveryCos', 'SuppliesMaterialsCogs'],
  },
  {
    type: 'Expense',
    classification: 'Expense',
    subTypes: [
      'AdvertisingPromotional', 'Auto', 'BadDebts', 'BankCharges', 'CharitableContributions', 'CommissionsAndFees', 'CostOfLabor', 'DuesSubscriptions',
      'Entertainment', 'EntertainmentMeals', 'EquipmentRental', 'FinanceCosts', 'GlobalTaxExpense', 'Insurance', 'InterestPaid',
      'LegalProfessionalFees', 'OfficeExpenses', 'OfficeGeneralAdministrativeExpenses', 'OtherBusinessExpenses', 'OtherMiscellaneousServiceCost',
      'PayrollExpenses', 'PromotionalMeals', 'RentOrLeaseOfBuildings', 'RepairMaintenance', 'ShippingFreightDelivery', 'SuppliesMaterials',
      'TaxesPaid', 'Travel', 'TravelMeals', 'UnappliedCashBillPaymentExpense', 'Utilities',
    ],
  },
  { type: 'Other Expense', classification: 'Expense', subTypes: ['Amortization', 'Depreciation', 'ExchangeGainOrLoss', 'OtherMiscellaneousExpense', 'PenaltiesSettlements'] },
];

const byType = (type) => QBO_ACCOUNT_TYPES.find((t) => t.type === type);

/** Classification QuickBooks gives the type, or '' for a type not in the list. */
export function classificationOf(type) {
  const t = byType(type);
  return t ? t.classification : '';
}

/** Detail types the account type allows ([] for a type not in the list). */
export function subTypesOf(type) {
  const t = byType(type);
  return t ? t.subTypes : [];
}

/** Readable detail type: "OfficeGeneralAdministrativeExpenses" -> "Office General Administrative Expenses", "Investment_Other" -> "Investment - Other". */
export function subTypeLabel(code) {
  return String(code || '')
    .split('_')
    .map((part) => part.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'))
    .join(' - ');
}
